import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import './HazardAssessmentHistory.css';
import API from './services/api';  // Add this
import { isDemoMode } from './utils/runtimeMode';

console.log('HazardAssessmentHistory module loaded');

const HazardAssessmentHistory = () => {
  console.log('HazardAssessmentHistory render start');
  const { user, loading: authLoading } = useAuth();
  console.log('HazardAssessmentHistory render: user=', user, 'authLoading=', authLoading);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10; // show 10 entries per page

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading

    // Some auth payloads store the login as `username` rather than `email`.
    if (!user || (!user.email && !user.username)) {
      setLoading(false); // Not logged in, stop loading spinner
      setHistory([]);
      return;
    }

    let url;
  console.log("🔑 User role:", user.role);
  console.log("🔑 User email:", user.email || user.username);

    const normalizedEmail = String(user.email || user.username || '').trim().toLowerCase();
    const hasCaoHistoryAccess = normalizedEmail === 'cao@diamondvalley.town'
      || (isDemoMode() && ['3p3cao@gmail.com', 'ctdemo.cao@proton.me'].includes(normalizedEmail));

    // Check ALL possible admin conditions
    if (
      user.role === 'ADMIN' || 
      user.role === 'admin' || 
      hasCaoHistoryAccess
    ) {
      url = '/api/hazards/history';
      console.log("🔑 Using admin URL");
    } else {
      // For regular users, use their username
      url = `/api/hazards/history?user=${user.username}`;
      console.log("🔑 Using regular user URL with username:", user.username);
    }

  // Rely on API instance defaults / interceptors to include the auth token.
  // This avoids timing and header-casing races where the manual header may
  // be missing on the first request and cause the endpoint to return no data.
    // Fetch with a small retry-on-empty mechanism to handle potential
    // timing races where the Authorization header isn't present yet.
    const maxRetries = 2;
    const retryDelayMs = 300;

    const fetchHistory = async (attempt = 0) => {
      setLoading(true);
      try {
        const res = await API.get(url);
        console.log('DEBUG HazardHistory response:', res && res.data);

        let items = [];
        if (!res || res.data === undefined || res.data === null) {
          items = [];
        } else if (Array.isArray(res.data)) {
          items = res.data;
        } else if (Array.isArray(res.data.history)) {
          items = res.data.history;
        } else if (Array.isArray(res.data.items)) {
          items = res.data.items;
        } else if (res.data.success && Array.isArray(res.data.rows)) {
          items = res.data.rows;
        } else {
          items = [];
        }

        // If result is empty but we haven't exceeded retries, try again
        if (Array.isArray(items) && items.length === 0 && attempt < maxRetries) {
          console.log(`HazardHistory: empty result on attempt ${attempt + 1}, retrying in ${retryDelayMs}ms`);
          setTimeout(() => fetchHistory(attempt + 1), retryDelayMs);
          return;
        }

        setHistory(items);
        setPage(1);
      } catch (err) {
        console.error('Failed to fetch hazard history:', err);
        console.error('Error details:', err.response?.data);
        console.error('Error status:', err.response?.status);
        console.error('Error headers:', err.response?.headers);

        if (err.response?.status === 403) {
          console.error('🚫 ACCESS DENIED: You do not have permission to view hazard history');
          console.error('🚫 Your user role:', user?.role);
          console.error('🚫 Your username:', user?.username);
        }
      } finally {
        setLoading(false);
      }
    };

    // Start the first fetch attempt
    fetchHistory(0);
  }, [user, authLoading]);

  // Derive unique user list for filter dropdown
  const userOptions = Array.isArray(history)
    ? Array.from(new Set(history.map(h => h.USER).filter(Boolean))).sort()
    : [];

  // Apply filters in-memory
  const filteredHistory = Array.isArray(history)
    ? history.filter(entry => {
        let ok = true;

        // Filter by user (creator)
        if (selectedUser) {
          ok = ok && entry.USER === selectedUser;
        }

        // Combine date and time for comparison; fall back to date only
        const dateStr = entry.ASSESSMENT_DATE;
        const timeStr = entry.ASSESSMENT_TIME || '00:00:00';
        const fullDateTime = dateStr ? new Date(`${dateStr}T${timeStr}`) : null;

        // Helper: compare only on date part
        const sameOrAfter = (d, cmp) => {
          if (!d || !cmp) return true;
          const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          const cOnly = new Date(cmp.getFullYear(), cmp.getMonth(), cmp.getDate());
          return dOnly >= cOnly;
        };

        const sameOrBefore = (d, cmp) => {
          if (!d || !cmp) return true;
          const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          const cOnly = new Date(cmp.getFullYear(), cmp.getMonth(), cmp.getDate());
          return dOnly <= cOnly;
        };

        if (startDate && fullDateTime) {
          const start = new Date(startDate);
          ok = ok && sameOrAfter(fullDateTime, start);
        }

        if (endDate && fullDateTime) {
          const end = new Date(endDate);
          ok = ok && sameOrBefore(fullDateTime, end);
        }

        return ok;
      })
    : [];

  // Compute pagination boundaries on filtered data
  const totalItems = filteredHistory.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIdx = (page - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paged = filteredHistory.slice(startIdx, endIdx);

  if (authLoading || !user || loading) {
    return <div>Loading...</div>;
  }

  const handleViewPDF = async (assessmentId) => {
    try {
      const response = await API.get(`/api/hazards/pdf/${assessmentId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('PDF fetch error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      alert('Failed to fetch PDF. Please check console for details.');
    }
  };

  return (
    <div>
      <h2>Hazard Assessment History</h2>
      {/* Filters */}
      <div className="hazard-history-filters" style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontWeight: 500 }}>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 500 }}>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 500 }}>User</label>
          <select
            value={selectedUser}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Users</option>
            {userOptions.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
      </div>
  <table className="hazard-history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Summary</th>
            <th>User</th>
            <th>PDF</th>
          </tr>
        </thead>
        <tbody>
          {paged.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center' }}>No history available</td>
            </tr>
          ) : (
            paged.map(entry => (
              <tr key={entry.ASSESSMENT_ID}>
                <td>{entry.ASSESSMENT_DATE} {entry.ASSESSMENT_TIME}</td>
                <td>{entry.TALKING_POINTS}</td>
                <td>{entry.USER}</td>
                <td>
                  <button onClick={() => handleViewPDF(entry.ASSESSMENT_ID)}>
                    View PDF
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
        <div>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ marginLeft: 8 }}>Next</button>
        </div>
        <div>Page {page} of {totalPages} — {totalItems} entries</div>
      </div>
    </div>
  );
};

export default HazardAssessmentHistory;